import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  Tooltip,
  useTheme,
} from "@mui/material";
import {
  CloseOutlined,
  ZoomInMapOutlined,
  TimelineOutlined,
  AnalyticsOutlined,
} from "@mui/icons-material";
import dayjs from "dayjs";
import useStyles from "./styles";
import LineChartComponent from "../lineComponent";
import MultiMetricGraphComponent from "../saude/multiMetricGraphComponent";

const utc = require("dayjs/plugin/utc");
const timezone = require("dayjs/plugin/timezone");
dayjs.extend(utc);
dayjs.extend(timezone);
const timeZone = "America/Sao_Paulo";

export default function ModalChartHistory({
  open = false,
  onClose = () => {},
  title = "Histórico de Série Temporal",
  subtitle = "Análise detalhada do período",
  icon: IconComponent = TimelineOutlined,
  color = "blue",
  unit = "",
  formatValue = null,
  initialData = [],
  currentValue = null,
  chartType = "line", // "line" | "multiLine"
  itemNames = {},
  labelExtractor = null,
  valueMultiplier = 1,
  maxValuebps = null,
  minValuebps = 0,
}) {
  const theme = useTheme();
  const styles = useStyles(theme);

  const [chartData, setChartData] = useState(initialData);
  const [zoomRange, setZoomRange] = useState(null);

  // Sincroniza chartData sempre que initialData mudar ou o modal abrir
  useEffect(() => {
    if (open) {
      setChartData(initialData);
      setZoomRange(null);
    }
  }, [open, initialData]);

  const formatBytes = (val) => {
    if (val === null || val === undefined || val === "" || val === "-" || val === "N/A") return "-";
    const numVal = Number(val);

    if (formatValue && typeof formatValue === "function") {
      try {
        return formatValue(!isNaN(numVal) ? numVal : val, unit);
      } catch (e) {
        if (!isNaN(numVal)) return `${numVal.toFixed(2)} ${unit}`;
        return String(val);
      }
    }
    if (isNaN(numVal)) return String(val);
    if (unit === "bps" || unit === "Kbps" || unit === "Mbps") {
      if (numVal === 0) return "0 bps";
      const k = 1000;
      const sizes = ["bps", "Kbps", "Mbps", "Gbps", "Tbps"];
      const i = Math.max(0, Math.floor(Math.log(numVal) / Math.log(k)));
      return `${(numVal / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
    }
    return `${numVal.toFixed(2)} ${unit}`;
  };

  const toDayjs = (val) => {
    if (dayjs.isDayjs(val)) return val.tz(timeZone);
    const num = Number(val);
    if (isNaN(num)) return dayjs(val).tz(timeZone);
    return num > 1e11 ? dayjs(num).tz(timeZone) : dayjs.unix(num).tz(timeZone);
  };

  const isTimeInRange = (timeVal, startDayjs, endDayjs) => {
    const num = Number(timeVal);
    if (isNaN(num) || !timeVal) return true;
    if (num > 1e11) {
      return num >= startDayjs.valueOf() && num <= endDayjs.valueOf();
    } else {
      return num >= startDayjs.unix() && num <= endDayjs.unix();
    }
  };

  const handleAplicarZoom = (startVal, endVal) => {
    const s = toDayjs(startVal);
    const e = toDayjs(endVal);

    setZoomRange({
      start: s.format("DD/MM/YY HH:mm:ss"),
      end: e.format("DD/MM/YY HH:mm:ss"),
    });

    // Filtra os dados localmente no intervalo do zoom
    if (Array.isArray(initialData)) {
      const filtered = initialData.filter((item) => {
        const time = item.clock !== undefined ? item.clock : item.date;
        return isTimeInRange(time, s, e);
      });
      setChartData(filtered.length ? filtered : initialData);
    } else if (initialData && typeof initialData === "object") {
      const filteredObj = {};
      let hasPoints = false;
      Object.entries(initialData).forEach(([core, arr]) => {
        if (Array.isArray(arr)) {
          const filt = arr.filter((item) => {
            const time = item.clock !== undefined ? item.clock : item.date;
            return isTimeInRange(time, s, e);
          });
          filteredObj[core] = filt;
          if (filt.length) hasPoints = true;
        }
      });
      setChartData(hasPoints ? filteredObj : initialData);
    }
  };

  const handleResetZoom = () => {
    setZoomRange(null);
    setChartData(initialData);
  };

  const parseNumeric = (val) => {
    if (val === null || val === undefined || val === "" || val === "-" || val === "N/A") return NaN;
    if (typeof val === "number") return val;
    const cleanStr = String(val).replace(/,/g, ".");
    const match = cleanStr.match(/[-+]?[0-9]*\.?[0-9]+/);
    return match ? Number(match[0]) : NaN;
  };

  const calculateKpis = () => {
    if (!chartData) return { min: "-", max: "-", avg: "-", current: "-", count: 0 };

    let values = [];
    if (Array.isArray(chartData)) {
      values = chartData
        .map((item) => {
          if (item?.value !== undefined) return Number(item.value);
          const seriesKeys = Object.keys(item || {}).filter(
            (k) => k !== "date" && k !== "clock",
          );
          if (!seriesKeys.length) return NaN;
          return seriesKeys.reduce(
            (sum, k) => sum + (Number(item[k]) || 0),
            0,
          );
        })
        .filter((val) => !isNaN(val));
    } else if (typeof chartData === "object") {
      Object.values(chartData).forEach((arr) => {
        if (Array.isArray(arr)) {
          arr.forEach((entry) => {
            const val = Number(entry?.value);
            if (!isNaN(val)) values.push(val);
          });
        }
      });
    }

    if (!values.length) {
      return {
        min: "-",
        max: "-",
        avg: "-",
        current: currentValue !== null && currentValue !== undefined ? String(currentValue) : "-",
        count: 0,
      };
    }

    const minVal = Math.min(...values);
    const maxVal = Math.max(...values);
    const avgVal = values.reduce((acc, curr) => acc + curr, 0) / values.length;

    let currentFormatted = "-";
    if (typeof currentValue === "string" && isNaN(Number(currentValue)) && currentValue !== "-" && currentValue !== " - " && currentValue !== "N/A") {
      currentFormatted = currentValue;
    } else {
      const parsedCurr = parseNumeric(currentValue);
      const lastNum = !isNaN(parsedCurr) ? parsedCurr : values[values.length - 1];
      currentFormatted = formatBytes(lastNum * valueMultiplier);
    }

    return {
      min: formatBytes(minVal * valueMultiplier),
      max: formatBytes(maxVal * valueMultiplier),
      avg: formatBytes(avgVal * valueMultiplier),
      current: currentFormatted,
      count: values.length,
    };
  };

  const kpis = calculateKpis();
  const iconColor = theme.palette.cardGraphic?.graphColor?.[color] || theme.palette.primary.main;

  return (
    <Modal open={open} onClose={onClose} sx={styles.modal}>
      <Box sx={styles.container}>
        {/* Header */}
        <Box sx={styles.header}>
          <Box sx={styles.headerLeft}>
            <Box sx={{ ...styles.iconBox, backgroundColor: `${iconColor}22`, border: `1px solid ${iconColor}44` }}>
              {IconComponent ? (
                <IconComponent sx={{ color: iconColor, fontSize: 28 }} />
              ) : (
                <AnalyticsOutlined sx={{ color: iconColor, fontSize: 28 }} />
              )}
            </Box>
            <Box sx={styles.titleContainer}>
              <Typography sx={styles.titleText}>{title}</Typography>
              <Typography sx={styles.subtitleText}>{subtitle}</Typography>
            </Box>
          </Box>

          <Box sx={styles.headerRight}>
            <Tooltip title="Fechar modal">
              <IconButton size="small" sx={styles.closeButton} onClick={onClose}>
                <CloseOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Content Body */}
        <Box sx={styles.content}>
          {/* KPI Summary Banner */}
          <Box sx={styles.kpiContainer}>
            <Box sx={styles.kpiCard}>
              <Typography sx={styles.kpiLabel}>Valor Atual</Typography>
              <Typography sx={{ ...styles.kpiValue, color: iconColor }}>{kpis.current}</Typography>
            </Box>
            <Box sx={styles.kpiCard}>
              <Typography sx={styles.kpiLabel}>Média (Avg)</Typography>
              <Typography sx={styles.kpiValue}>{kpis.avg}</Typography>
            </Box>
            <Box sx={styles.kpiCard}>
              <Typography sx={styles.kpiLabel}>Mínimo Registrado</Typography>
              <Typography sx={styles.kpiValue}>{kpis.min}</Typography>
            </Box>
            <Box sx={styles.kpiCard}>
              <Typography sx={styles.kpiLabel}>Pico Máximo</Typography>
              <Typography sx={styles.kpiValue}>{kpis.max}</Typography>
            </Box>
            <Box sx={styles.kpiCard}>
              <Typography sx={styles.kpiLabel}>Pontos Coletados</Typography>
              <Typography sx={styles.kpiValue}>{kpis.count}</Typography>
            </Box>
          </Box>

          {/* Chart Section */}
          <Box sx={styles.chartPaper}>
            <Box sx={styles.chartHeader}>
              <Box sx={{ ...styles.chartTitleBox, justifyContent: "space-between" , width: "100%"}}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Gráfico Detalhado de Evolução
                </Typography>
                {zoomRange && (
                  <Box sx={styles.zoomActiveBox}>
                    <ZoomInMapOutlined sx={{ fontSize: 16, color: theme.palette.warning.main }} />
                    <Typography fontSize="0.75rem" fontWeight={600} color="warning.main">
                      Zoom: {zoomRange.start} até {zoomRange.end}
                    </Typography>
                    <Button
                      size="small"
                      variant="text"
                      sx={{ minWidth: "auto", p: 0, textTransform: "none", fontSize: "0.75rem", ml: 1 }}
                      onClick={handleResetZoom}
                    >
                      Resetar
                    </Button>
                  </Box>
                )}
              </Box>
              {/* <Typography variant="caption" color="text.secondary">
                Dica: Clique e arraste no gráfico para aplicar zoom no intervalo desejado.
              </Typography> */}
            </Box>

            <Box sx={styles.chartContainer}>
              {chartType === "multiLine" ? (
                <MultiMetricGraphComponent
                  data={chartData}
                  dataNow={currentValue}
                  titleGraph={title}
                  unit={unit}
                  loading={false}
                  isZoomEnabled={true}
                  handleAplicarZoom={(startVal, endVal) => {
                    const startD = toDayjs(startVal);
                    const endD = toDayjs(endVal);
                    handleAplicarZoom(startD, endD);
                  }}
                  valueMultiplier={valueMultiplier}
                  itemNames={itemNames}
                  labelExtractor={labelExtractor}
                  formatValue={formatValue}
                  enableHistoryModal={false}
                />
              ) : (
                <LineChartComponent
                  data={chartData}
                  isZoomEnabled={true}
                  onZoomChange={(range) => {
                    const startD = toDayjs(range.start);
                    const endD = toDayjs(range.end);
                    handleAplicarZoom(startD, endD);
                  }}
                  maxTicks={15}
                  mainColor={theme.palette.cardGraphic?.graphColor?.[color] || iconColor}
                  mainLineColor={theme.palette.cardGraphic?.graphLineColor?.[color] || iconColor}
                  maxValuebps={maxValuebps}
                  minValuebps={minValuebps}
                  yOptions={{ tickFormatter: formatBytes }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}
